import { ServiceNextSteps } from '@/components/trinity/ServiceConversion';
import React from 'react';
import { useLocation } from 'react-router-dom';
import ReviewShowcase from '@/components/reviews/ReviewShowcase';
import { RATING_SUMMARY } from '@/lib/reviews';
import { Facebook, Instagram, Youtube, MapPin, Phone, Mail, Clock, MessageSquare, ShieldCheck, Star, CreditCard, ScanLine, Microscope, Zap } from 'lucide-react';
import { base44 } from '@/api/base44Client';

const FOOTER_MAP_SRC = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d739.2930167990238!2d-82.63153612183444!3d28.186339886218033!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88c294197249b103%3A0x5861078ce2db4fab!2sTrinity%20Dental%20Arts%20-%20Expert%20Implant%20and%20Cosmetic%20Dentistry!5e0!3m2!1sen!2sus!4v1770657035750!5m2!1sen!2sus";

// The Google Maps embed pulls ~600 KB of Maps JavaScript into every page.
// `loading="lazy"` alone did not help: while the home page's lazy sections
// are still loading, the footer sits close enough to the viewport for the
// browser to start the iframe anyway. The iframe is therefore only created
// once the footer map actually approaches the viewport.
function LazyFooterMap() {
  const holderRef = React.useRef(null);
  const [shouldLoad, setShouldLoad] = React.useState(false);
  React.useEffect(() => {
    const node = holderRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') { setShouldLoad(true); return undefined; }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setShouldLoad(true); observer.disconnect(); }
    }, { rootMargin: '400px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={holderRef} className="bg-slate-800" style={{ height: 220 }} aria-hidden={!shouldLoad}>
      {shouldLoad && (
        <iframe
          src={FOOTER_MAP_SRC}
          width="100%" height="220" frameBorder="0" allowFullScreen loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Trinity Dental Arts Location"
          className="pointer-events-none" />
      )}
    </div>
  );
}

export default function Footer() {
  const { pathname } = useLocation();
  return (
    <>
    <ServiceNextSteps />
    <footer className="bg-slate-900 text-white">

      {/* Main Footer Grid */}
      <div className="container mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">

          {/* Column 1: Brand + Social */}
          <div>
            <h3 className="text-2xl font-light mb-2">
              Trinity <span className="font-serif italic text-amber-400">Dental Arts</span>
            </h3>
            <p className="text-slate-300 text-base leading-relaxed mb-5">
              Expert implant and cosmetic dentistry with Dr. Xhoana Gjelaj. Serving Trinity, FL.
            </p>
            <p className="text-slate-400 text-sm font-medium mb-3 uppercase tracking-wide">Follow Us</p>
            <div className="flex flex-wrap gap-2">
              <a href="https://www.facebook.com/trinitydentalarts" target="_blank" rel="noopener noreferrer me"
                className="w-11 h-11 bg-slate-700 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Follow us on Facebook">
                <Facebook className="w-5 h-5 text-slate-200" />
              </a>
              <a href="https://www.youtube.com/@trinitydentalarts" target="_blank" rel="noopener noreferrer me"
                className="w-11 h-11 bg-slate-700 hover:bg-red-600 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Subscribe on YouTube">
                <Youtube className="w-5 h-5 text-slate-200" />
              </a>
              <a href="https://www.instagram.com/trinitydentalarts" target="_blank" rel="noopener noreferrer me"
                className="w-11 h-11 bg-slate-700 hover:bg-pink-600 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Follow us on Instagram">
                <Instagram className="w-5 h-5 text-slate-200" />
              </a>
              <a href="https://www.linkedin.com/company/trinity-dental-arts" target="_blank" rel="noopener noreferrer me"
                className="w-11 h-11 bg-slate-700 hover:bg-blue-700 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Connect on LinkedIn">
                <svg className="w-5 h-5 text-slate-200 fill-current" viewBox="0 0 24 24">
                  <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.5c0-1.08-.4-1.84-1.34-1.84a1.44 1.44 0 00-1.35 1c-.07.17-.09.42-.09.67V19h-3v-9h2.86v1.23h.04c.4-.64 1.56-1.31 3.21-1.31 2.39 0 4.18 1.56 4.18 4.9z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Column 2: Contact */}
          <div>
            <h4 className="text-base font-semibold text-white mb-4 uppercase tracking-wide">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a href="https://maps.app.goo.gl/dnWeZn2SyEMD1BQ48" target="_blank" rel="noopener noreferrer"
                  className="flex items-start gap-3 text-slate-300 hover:text-amber-400 transition-colors"
                  onClick={() => base44.analytics.track({ eventName: 'get_directions_clicked', properties: { location: 'footer_contact' } })}>
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-400" />
                  <span className="text-base leading-snug">11105 Trinity Blvd<br />Trinity, FL 34655</span>
                </a>
              </li>
              <li>
                <a href="tel:+17272286846" aria-label="Call (727) 228-6846" className="flex items-center gap-3 text-slate-300 hover:text-amber-400 transition-colors">
                  <Phone className="w-5 h-5 flex-shrink-0 text-amber-400" />
                  <span className="text-base font-medium">(727) 228-6846</span>
                </a>
              </li>
              <li>
                <a href="sms:7276078699" className="flex items-center gap-3 text-slate-300 hover:text-amber-400 transition-colors">
                  <MessageSquare className="w-5 h-5 flex-shrink-0 text-amber-400" />
                  <span className="text-base font-medium">SMS: (727) 607-8699</span>
                </a>
              </li>
              <li>
                <a href="mailto:info@trinitydentalarts.com" className="flex items-center gap-3 text-slate-300 hover:text-amber-400 transition-colors">
                  <Mail className="w-5 h-5 flex-shrink-0 text-amber-400" />
                  <span className="text-base break-all">info@trinitydentalarts.com</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-300">
                <Clock className="w-5 h-5 flex-shrink-0 text-amber-400 mt-0.5" />
                <div className="text-base">
                  <p>Mon–Thu: <strong className="text-white">9am – 5pm</strong></p>
                  <p>Fri–Sun: <strong className="text-slate-400">Closed</strong></p>
                </div>
              </li>
            </ul>
          </div>

          {/* Column 3: Quick Links */}
          <div>
            <h4 className="text-base font-semibold text-white mb-4 uppercase tracking-wide">Our Services</h4>
            <ul className="space-y-3">
              {[
                { label: 'Dental Implants', href: '/dental-implants' },
                { label: 'Cosmetic Dentistry', href: '/cosmetic-dentistry' },
                { label: 'All-on-4 / All-on-6', href: '/all-on-x' },
                { label: 'General Dentistry', href: '/general-dentistry' },
                { label: 'Emergency Dentistry', href: '/emergency-dentistry' },
                { label: 'About Dr. Gjelaj', href: '/meet-the-doctor' },
                { label: 'Contact Us', href: '/contact-us' },
                { label: 'Patient Reviews', href: '/patient-reviews' },
                { label: 'FAQ', href: '/faq' },
                ].map(link => (
                <li key={link.href}>
                  <a href={link.href} className="text-slate-300 hover:text-amber-400 transition-colors text-base">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Map */}
          <div>
            <h4 className="text-base font-semibold text-white mb-4 uppercase tracking-wide">Find Us</h4>
            <div className="rounded-lg overflow-hidden shadow-lg mb-4">
              <a href="https://maps.app.goo.gl/dnWeZn2SyEMD1BQ48" target="_blank" rel="noopener noreferrer"
                onClick={() => base44.analytics.track({ eventName: 'get_directions_clicked', properties: { location: 'footer_map' } })}
                className="block relative group">
                <LazyFooterMap />
                <div className="absolute inset-0 bg-transparent group-hover:bg-amber-600/10 transition-colors flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white px-4 py-2 rounded-lg shadow-lg text-slate-900 font-medium text-sm">
                    Get Directions →
                  </span>
                </div>
              </a>
            </div>
            <a
              href="/book-appointment"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center bg-amber-600 hover:bg-amber-500 text-white font-semibold text-base py-3 px-4 rounded-lg transition-colors">
              Book an Appointment →
            </a>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">The $300 Signature Consultation is a 90-minute visit with Dr. Gjelaj, including an exam, CBCT, X-rays, an intraoral scan and a written treatment plan. The fee is credited toward treatment begun within 30 days.</p>
          </div>
        </div>

        {pathname.replace(/\/$/, '') !== '/patient-reviews' && <div className="py-6 border-t border-slate-700 mb-6"><ReviewShowcase limit={3} /></div>}

        {/* ── Trust, Credentials & Compliance band ── */}
        <div className="border-t border-slate-700">

          {/* Why Patients Choose Us */}
          <div className="py-8">
            <p className="text-center text-slate-400 text-xs uppercase tracking-widest mb-5">Why Patients Choose Trinity Dental Arts</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { icon: Star, label: RATING_SUMMARY, href: '/patient-reviews' },
                { icon: ShieldCheck, label: 'Board-Certified Implantologist', href: '/meet-the-doctor' },
                { icon: Microscope, label: 'In-House Dental Lab', href: '/in-house-lab' },
                { icon: CreditCard, label: 'Financing Available', href: '/financing-options' },
                { icon: ScanLine, label: '3D CBCT Imaging', href: '/our-technology' },
                { icon: Zap, label: 'Same-Day Emergencies', href: '/emergency-dentistry' },
              ].map(({ icon: Icon, label, href }) => (
                <a key={label} href={href}
                  className="flex flex-col items-center text-center gap-2 px-3 py-4 bg-slate-800/60 border border-slate-700 rounded-lg hover:border-amber-600/50 hover:bg-slate-800 transition-colors group">
                  <Icon className="w-6 h-6 text-amber-400 group-hover:scale-110 transition-transform" aria-hidden="true" />
                  <span className="text-slate-300 text-xs font-medium leading-snug">{label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* ── Credentials & Compliance — lean strip ── */}
          <div className="border-t border-slate-800 py-6 flex flex-col gap-4">
            {/* Credentials */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5">
              <span className="text-slate-400 text-xs uppercase tracking-widest sm:flex-shrink-0 text-center sm:text-left">Dr. Gjelaj's Credentials</span>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 justify-center sm:justify-start">
                {[
                  { abbr: 'MAGD', full: 'Master of the Academy of General Dentistry' },
                  { abbr: 'DICOI', full: 'Diplomate of the International Congress of Oral Implantologists' },
                  { abbr: 'FAAID', full: 'Fellow of the American Academy of Implant Dentistry' },
                  { abbr: 'DABOI/ID', full: 'Diplomate of the American Board of Oral Implantology / Implant Dentistry' },
                ].map(({ abbr, full }) => (
                  <span key={abbr} title={full} className="flex items-center gap-1.5 cursor-default">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    <span className="text-amber-400 font-semibold text-sm tracking-wide">{abbr}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Security & Compliance */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5">
              <span className="text-slate-400 text-xs uppercase tracking-widest sm:flex-shrink-0 text-center sm:text-left">Security &amp; Compliance</span>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 justify-center sm:justify-start">
                {[
                  { label: 'ISO 27001', title: 'ISO 27001 Certified' },
                  { label: 'Encrypted', title: 'Encrypted at Rest & in Transit' },
                  { label: 'HIPAA', title: 'HIPAA Compliant' },
                  { label: 'SSL', title: 'SSL Secured' },
                  { label: 'ADA', title: 'ADA Compliant — WCAG 2.1 Level AA' },
                ].map(({ label, title }) => (
                  <span key={label} title={title} className="flex items-center gap-1.5 cursor-default">
                    <ShieldCheck className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" aria-hidden="true" />
                    <span className="text-slate-300 text-xs font-medium">{label}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="border-t border-slate-700 pt-5 flex flex-col md:flex-row md:justify-between items-center gap-3 md:gap-6">
          <span className="text-slate-400 text-sm text-center md:text-left">© {new Date().getFullYear()} Trinity Dental Arts. All rights reserved.</span>
          <span className="text-slate-400 text-xs md:text-sm text-center md:text-left">For informative purposes only, not medical advice.</span>
          <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center items-center">
            <a href="/privacy-policy" className="text-slate-400 text-sm hover:text-amber-400 transition-colors">Privacy</a>
            <a href="/unsubscribe" className="text-slate-400 text-sm hover:text-amber-400 transition-colors">Email Preferences</a>
            <button type="button" onClick={() => window.dispatchEvent(new Event("tda-cookie-preferences"))} className="text-slate-400 text-sm hover:text-amber-400 transition-colors">Cookies</button>
            <a href="/terms-of-service" className="text-slate-400 text-sm hover:text-amber-400 transition-colors">Terms</a>
            <a href="/resources/office-policies" className="text-slate-400 text-sm hover:text-amber-400 transition-colors">Policies</a>
            <a href="/accessibility-statement" className="text-slate-400 text-sm hover:text-amber-400 transition-colors">Accessibility</a>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}